// POS coupon input: validate code → show discount → emit {code, amountOff}.
// mode="chip"   → labeled button that expands inline into input field
// mode="inline" → full inline form (used in payment panel)
import { useState, useRef, useEffect } from "react";
import { apiGet } from "@/lib/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tag, X, Loader2 } from "lucide-react";

interface CouponResult {
  code: string;
  amountOff: number;
}

interface Props {
  subtotal: number;
  onApply: (result: CouponResult | null) => void;
  applied: CouponResult | null;
  mode?: "chip" | "inline";
}

function fmtVND(n: number) {
  return new Intl.NumberFormat("vi-VN").format(Math.round(n)) + " ₫";
}

export function CouponInput({ subtotal, onApply, applied, mode = "chip" }: Props) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (expanded) {
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [expanded]);

  async function handleApply() {
    if (!code.trim()) return;
    setLoading(true);
    setError("");
    try {
      const res = await apiGet<{ coupon: unknown; amountOff: number }>(
        `/v1/coupons/validate?code=${encodeURIComponent(code.trim())}&subtotal=${subtotal}`,
      );
      onApply({ code: code.trim().toUpperCase(), amountOff: res.amountOff });
      setCode("");
      setExpanded(false);
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message ?? "Mã không hợp lệ";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  function handleRemove() {
    onApply(null);
    setCode("");
    setError("");
    setExpanded(false);
  }

  function handleCancel() {
    setCode("");
    setError("");
    setExpanded(false);
  }

  // ── Chip mode: labeled button → inline expand ─────────────────────────────
  if (mode === "chip") {
    if (applied) {
      return (
        <div className="flex items-center gap-1 shrink-0 rounded-lg border border-green-300 bg-green-50 pl-2.5 pr-1 py-1 text-xs text-green-700">
          <Tag size={12} className="shrink-0" />
          <span className="font-mono font-medium">{applied.code}</span>
          <span className="text-green-600">-{fmtVND(applied.amountOff)}</span>
          <button
            onClick={handleRemove}
            className="ml-0.5 rounded p-0.5 text-green-500 hover:text-green-800 hover:bg-green-100 transition-colors"
          >
            <X size={12} />
          </button>
        </div>
      );
    }

    if (expanded) {
      return (
        <div className="flex items-center gap-1.5 shrink-0">
          <div className="relative">
            <Input
              ref={inputRef}
              value={code}
              onChange={(e) => { setCode(e.target.value.toUpperCase()); setError(""); }}
              placeholder="Nhập mã..."
              className={`h-8 w-36 text-xs font-mono pr-7 ${error ? "border-destructive" : ""}`}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleApply();
                if (e.key === "Escape") handleCancel();
              }}
            />
            {code && (
              <button
                onClick={() => { setCode(""); setError(""); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X size={12} />
              </button>
            )}
          </div>
          <Button
            size="sm"
            className="h-8 text-xs px-3"
            onClick={handleApply}
            disabled={loading || !code.trim()}
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : "OK"}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-8 w-8 p-0 text-muted-foreground"
            onClick={handleCancel}
          >
            <X size={14} />
          </Button>
        </div>
      );
    }

    return (
      <Button
        size="sm"
        variant="outline"
        className="h-8 gap-1.5 text-xs shrink-0"
        onClick={() => setExpanded(true)}
      >
        <Tag size={14} />
        <span className="hidden sm:inline">Voucher</span>
      </Button>
    );
  }

  // ── Inline mode (original behavior) ──────────────────────────────────────
  if (applied) {
    return (
      <div className="flex items-center justify-between rounded-md border border-green-300 bg-green-50 px-3 py-2 text-sm">
        <div className="flex items-center gap-2">
          <Tag size={14} className="text-green-600" />
          <span className="font-mono font-medium text-green-700">{applied.code}</span>
          <span className="text-green-600">-{fmtVND(applied.amountOff)}</span>
        </div>
        <button onClick={handleRemove} className="text-muted-foreground hover:text-foreground">
          <X size={14} />
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <div className="flex gap-2">
        <Input
          value={code}
          onChange={(e) => { setCode(e.target.value.toUpperCase()); setError(""); }}
          placeholder="Mã giảm giá..."
          className="text-sm font-mono"
          onKeyDown={(e) => e.key === "Enter" && handleApply()}
        />
        <Button
          variant="outline"
          size="sm"
          onClick={handleApply}
          disabled={loading || !code.trim()}
          className="shrink-0"
        >
          {loading ? "..." : "Áp dụng"}
        </Button>
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
